/**
 * WebSocket manager with auto-reconnect.
 * Usage: const ws = createWebSocket('/ws/notifications', onMessage)
 */

// Strip any accidental trailing path segments (e.g. VITE_WS_URL set to "ws://host/ws" instead of "ws://host")
const WS_BASE = ((import.meta.env['VITE_WS_URL'] as string) ?? 'ws://localhost:8001').replace(/\/ws\/?$/, '')

export type WsMessageHandler = (event: MessageEvent) => void

export interface ManagedWebSocket {
  send: (data: string | object) => void
  close: () => void
}

export function createWebSocket(
  path: string,
  onMessage: WsMessageHandler,
  options: { maxRetries?: number; retryDelayMs?: number } = {},
): ManagedWebSocket {
  const { maxRetries = 5, retryDelayMs = 2000 } = options
  let ws: WebSocket | null = null
  let retries = 0
  let closed = false

  function connect() {
    ws = new WebSocket(`${WS_BASE}${path}`)

    ws.onmessage = onMessage

    ws.onclose = (event) => {
      if (closed) return
      if (retries < maxRetries && !event.wasClean) {
        retries++
        const delay = retryDelayMs * Math.pow(1.5, retries - 1)
        console.warn(`[WS] Disconnected. Reconnecting in ${delay}ms (attempt ${retries})…`)
        setTimeout(connect, delay)
      }
    }

    ws.onerror = (err) => {
      console.error('[WS] Error:', err)
    }

    ws.onopen = () => {
      retries = 0
    }
  }

  connect()

  return {
    send(data) {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(typeof data === 'string' ? data : JSON.stringify(data))
      }
    },
    close() {
      closed = true
      ws?.close()
    },
  }
}
